import { useState, useMemo } from "react";
import { useUserRoles, useAddUserRole, useRemoveUserRole } from "../api/users";
import { useCurrentUser } from "../api/auth";
import { useRoles } from "../api/roles";
import { useTeams } from "../api/teams";
import { filterAssignableRoles, getHighestRole } from "../shared/roleHierarchy";
import "./guest-memberships-panel.css";

interface GuestMembershipsPanelProps {
    userId: string;
    userName: string;
    onClose: () => void;
}

export const GuestMembershipsPanel = ({
    userId,
    userName,
    onClose,
}: GuestMembershipsPanelProps): JSX.Element => {
    const [selectedRole, setSelectedRole] = useState<string>("");
    const [selectedTeamId, setSelectedTeamId] = useState<string>("");

    const { data: currentUser } = useCurrentUser();
    const { data: currentUserRoles = [] } = useUserRoles(currentUser?.id ?? "");
    const { data: roles = [] } = useRoles();
    const isEditingSelf = !!currentUser && userId === currentUser.id;
    const currentUserHighestRole = useMemo(
        () => getHighestRole(currentUserRoles.map((r) => r.role)),
        [currentUserRoles]
    );
    const assignableRoles = useMemo(
        () =>
            currentUserHighestRole
                ? filterAssignableRoles(roles, currentUserHighestRole)
                : roles.filter((r) => r.value === "Distributor"),
        [roles, currentUserHighestRole]
    );
    const { data: teams = [] } = useTeams();
    const { data: userRoles = [], refetch } = useUserRoles(userId);
    const addRoleMutation = useAddUserRole();
    const removeRoleMutation = useRemoveUserRole();

    // Separate primary and guest memberships
    const primaryMembership = userRoles.find((r) => r.isPrimary);
    const guestMemberships = userRoles.filter((r) => r.isGuest && !r.isPrimary);

    const handleAddGuestMembership = async () => {
        if (!selectedRole || !selectedTeamId) {
            alert("Välj både roll och team");
            return;
        }

        try {
            await addRoleMutation.mutateAsync({
                id: userId,
                role: selectedRole,
                teamId: selectedTeamId,
                isGuest: true,
            });
            setSelectedRole("");
            setSelectedTeamId("");
            await refetch();
        } catch (error: any) {
            const message = error?.response?.data?.message || "Ett fel uppstod";
            alert(`Kunde inte lägga till gästmedlemskap: ${message}`);
        }
    };

    const handleRemoveGuestMembership = async (roleId: string) => {
        if (!confirm("Är du säker på att du vill ta bort detta gästmedlemskap?")) {
            return;
        }

        try {
            await removeRoleMutation.mutateAsync({
                userId,
                roleId,
            });
            await refetch();
        } catch (error: any) {
            const message = error?.response?.data?.message || "Ett fel uppstod";
            alert(`Kunde inte ta bort gästmedlemskap: ${message}`);
        }
    };

    return (
        <div className="guest-memberships-overlay" onClick={onClose}>
            <div className="guest-memberships-panel" onClick={(e) => e.stopPropagation()}>
                <div className="guest-memberships-panel__header">
                    <h3>Gästmedlemskap för {userName}</h3>
                    <button
                        className="guest-memberships-panel__close-button"
                        onClick={onClose}
                        aria-label="Stäng"
                    >
                        ×
                    </button>
                </div>

                <div className="guest-memberships-panel__content">
                    {primaryMembership && (
                        <div className="guest-memberships-panel__primary">
                            <h4>Primärt team</h4>
                            <div className="membership-card membership-card--primary">
                                <div className="membership-card__info">
                                    <strong>{primaryMembership.teamName}</strong>
                                    <span className="membership-card__role">{primaryMembership.role}</span>
                                </div>
                                <span className="membership-card__badge">Primär</span>
                            </div>
                        </div>
                    )}

                    <div className="guest-memberships-panel__guests">
                        <h4>Gästmedlemskap</h4>
                        {guestMemberships.length === 0 ? (
                            <p className="guest-memberships-panel__empty">Inga gästmedlemskap</p>
                        ) : (
                            <div className="guest-memberships-panel__list">
                                {guestMemberships.map((membership) => (
                                    <div key={membership.teamMembershipId} className="membership-card">
                                        <div className="membership-card__info">
                                            <strong>{membership.teamName}</strong>
                                            <span className="membership-card__role">{membership.role}</span>
                                        </div>
                                        <div className="membership-card__actions">
                                            <span className="membership-card__badge membership-card__badge--guest">
                                                Gäst
                                            </span>
                                            <button
                                                className="membership-card__remove-button"
                                                onClick={() => handleRemoveGuestMembership(membership.teamMembershipId)}
                                                disabled={removeRoleMutation.isPending || isEditingSelf}
                                                title={isEditingSelf ? "Du kan inte ta bort gästmedlemskap för dig själv" : "Ta bort gästmedlemskap"}
                                            >
                                                Ta bort
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="guest-memberships-panel__add">
                        <h4>Lägg till gästmedlemskap</h4>
                        {isEditingSelf ? (
                            <p className="guest-memberships-panel__cannot-edit-self">
                                Du kan inte lägga till gästmedlemskap för dig själv.
                            </p>
                        ) : (
                        <div className="guest-memberships-panel__form">
                            <div className="form-group">
                                <label htmlFor="guest-team">Team:</label>
                                <select
                                    id="guest-team"
                                    value={selectedTeamId}
                                    onChange={(e) => setSelectedTeamId(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="">Välj team...</option>
                                    {teams
                                        .filter((team) => team.id !== primaryMembership?.teamId)
                                        .map((team) => (
                                            <option key={team.id} value={team.id}>
                                                {team.name}
                                            </option>
                                        ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="guest-role">Roll:</label>
                                <select
                                    id="guest-role"
                                    value={selectedRole}
                                    onChange={(e) => setSelectedRole(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="">Välj roll...</option>
                                    {assignableRoles.map((role) => (
                                        <option key={role.value} value={role.value}>
                                            {role.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <button
                                type="button"
                                className="guest-memberships-panel__add-button"
                                onClick={handleAddGuestMembership}
                                disabled={!selectedRole || !selectedTeamId || addRoleMutation.isPending}
                            >
                                {addRoleMutation.isPending ? "Lägger till..." : "Lägg till gästmedlemskap"}
                            </button>
                        </div>
                        )}
                    </div>
                </div>

                <div className="guest-memberships-panel__footer">
                    <button
                        type="button"
                        className="guest-memberships-panel__close-footer-button"
                        onClick={onClose}
                    >
                        Stäng
                    </button>
                </div>
            </div>
        </div>
    );
};